import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Grid,
  Autocomplete,
  IconButton,
  Typography,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { Article } from '../types';
import { Client } from '../types/client';
import { StockReturnNew, StockReturnNewUpdate, StockReturnItemCreate } from '../types/stockReturnNew';
import { stockReturnsAPI } from '../services/api';
import { extractErrorMessage } from '../utils/errorHandler';
import useAlert from '../hooks/useAlert';
import AlertDialog from './AlertDialog';
import ConfirmationDialog, { ConfirmationField } from './ConfirmationDialog';

interface StockReturnEditProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  returnEntry: StockReturnNew | null;
  articles: Article[];
  clients: Client[];
}

interface ArticleLineItem {
  article_id: number;
  article?: Article;
  quantity: number | null | string;
  unit_price: number | null | string;
}

const StockReturnEdit: React.FC<StockReturnEditProps> = ({
  open,
  onClose,
  onSuccess,
  returnEntry,
  articles,
  clients,
}) => {
  const { alertState, showError, showSuccess, hideAlert } = useAlert();
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    bon_retour_number: '',
    client_id: null as number | null,
  });

  const [articleLines, setArticleLines] = useState<ArticleLineItem[]>([
    {
      article_id: 0,
      quantity: null,
      unit_price: null,
    },
  ]);

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load stock return data when dialog opens
  useEffect(() => {
    const loadStockReturnData = async () => {
      if (returnEntry && open) {
        try {
          const dateStr = returnEntry.date.split('T')[0];
          setFormData({
            date: dateStr,
            bon_retour_number: returnEntry.bon_retour_number,
            client_id: returnEntry.client_id,
          });

          // Map items to article lines from the parent-child structure
          const lines: ArticleLineItem[] = (returnEntry.items || []).map((item) => {
            const article = articles.find((a) => a.id === item.article_id);
            return {
              article_id: item.article_id,
              quantity: Number(item.quantity),
              unit_price: Number(item.unit_price),
              article: article,
            };
          });

          if (lines.length > 0) {
            setArticleLines(lines);
          } else {
            setArticleLines([{
              article_id: 0,
              quantity: null,
              unit_price: null,
            }]);
          }
        } catch (error) {
          console.error('Error loading stock return data:', error);
          showError('Erreur lors du chargement des données');
        }
      }
    };

    loadStockReturnData();
  }, [returnEntry, open, articles]);

  const formatNumber = (value: any, decimals: number = 2): string => {
    return Number(value || 0).toFixed(decimals);
  };

  const handleAddArticleLine = () => {
    setArticleLines([
      ...articleLines,
      {
        article_id: 0,
        quantity: null,
        unit_price: null,
      },
    ]);
  };

  const handleRemoveArticleLine = (index: number) => {
    if (articleLines.length > 1) {
      setArticleLines(articleLines.filter((_, i) => i !== index));
    }
  };

  const handleArticleChange = (index: number, article: Article | null) => {
    const newLines = [...articleLines];

    if (article) {
      newLines[index] = {
        ...newLines[index],
        article_id: article.id,
        article: article,
      };
    } else {
      newLines[index] = {
        article_id: 0,
        quantity: null,
        unit_price: null,
      };
    }

    setArticleLines(newLines);
  };

  const handleArticleFieldChange = (
    index: number,
    field: keyof ArticleLineItem,
    value: any
  ) => {
    const newLines = [...articleLines];
    newLines[index] = {
      ...newLines[index],
      [field]: value,
    };
    setArticleLines(newLines);
  };

  const calculateGrandTotal = (): number => {
    return articleLines.reduce((total, line) => {
      const quantity = typeof line.quantity === 'string'
        ? parseFloat(line.quantity.replace(',', '.')) || 0
        : (line.quantity ?? 0);
      const unitPrice = typeof line.unit_price === 'string'
        ? parseFloat(line.unit_price.replace(',', '.')) || 0
        : (line.unit_price ?? 0);
      const lineTotal = quantity * unitPrice;
      return total + lineTotal;
    }, 0);
  };

  const handleSubmitClick = () => {
    // Validate all required fields
    if (!formData.bon_retour_number || !formData.bon_retour_number.trim()) {
      showError('Veuillez saisir le numéro de BON RETOUR');
      return;
    }

    if (!formData.client_id) {
      showError('Veuillez sélectionner un client');
      return;
    }

    // Validate article lines
    for (let i = 0; i < articleLines.length; i++) {
      const line = articleLines[i];

      if (!line.article_id || line.article_id === 0) {
        showError(`Veuillez sélectionner un article pour la ligne ${i + 1}`);
        return;
      }

      const quantity = typeof line.quantity === 'string'
        ? parseFloat(line.quantity.replace(',', '.'))
        : line.quantity;

      if (quantity === null || isNaN(quantity as number) || (quantity as number) <= 0) {
        showError(`La quantité doit être supérieure à 0 pour la ligne ${i + 1}`);
        return;
      }

      const unitPrice = typeof line.unit_price === 'string'
        ? parseFloat(line.unit_price.replace(',', '.'))
        : line.unit_price;

      if (unitPrice === null || isNaN(unitPrice as number) || (unitPrice as number) <= 0) {
        showError(`Le prix unitaire doit être supérieur à 0 pour la ligne ${i + 1}`);
        return;
      }
    }

    // Show confirmation dialog
    setShowConfirmDialog(true);
  };

  const handleConfirmSubmit = async () => {
    if (!returnEntry || !returnEntry.id) return;

    setIsSubmitting(true);

    try {
      // Build items array for the new API format
      const items: StockReturnItemCreate[] = articleLines.map((line) => {
        const quantity = typeof line.quantity === 'string'
          ? parseFloat(line.quantity.replace(',', '.'))
          : (line.quantity ?? 0);
        const unitPrice = typeof line.unit_price === 'string'
          ? parseFloat(line.unit_price.replace(',', '.'))
          : (line.unit_price ?? 0);
        return {
          article_id: line.article_id,
          quantity: Math.round(quantity * 100) / 100,
          unit_price: Math.round(unitPrice * 100) / 100,
        };
      });

      // Update using the new API format
      const updateData: StockReturnNewUpdate = {
        bon_retour_number: formData.bon_retour_number,
        date: formData.date || new Date().toISOString().split('T')[0],
        client_id: formData.client_id!,
        items: items,
      };

      const result = await stockReturnsAPI.update(returnEntry.id, updateData);
      setShowConfirmDialog(false);
      showSuccess(
        `Retour de stock modifié avec succès! ${result.total_articles || result.items?.length || 0} article(s), Total: ${formatNumber(result.grand_total)} MAD`
      );
      onSuccess();
      handleClose();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getConfirmationFields = (): ConfirmationField[] => {
    const fields: ConfirmationField[] = [];

    fields.push({ label: 'Date', value: formData.date });
    fields.push({ label: 'BON RETOUR N°', value: formData.bon_retour_number });

    if (formData.client_id) {
      const client = clients.find((c) => c.id === formData.client_id);
      if (client) {
        fields.push({ label: 'Client', value: `${client.code} - ${client.name}` });
      }
    }

    fields.push({ label: '---', value: '---' });
    fields.push({ label: 'Articles', value: `${articleLines.length}`, bold: true });

    articleLines.forEach((line, index) => {
      const article = articles.find((a) => a.id === line.article_id);
      if (article) {
        const quantity = typeof line.quantity === 'string'
          ? parseFloat(line.quantity.replace(',', '.')) || 0
          : (line.quantity ?? 0);
        const unitPrice = typeof line.unit_price === 'string'
          ? parseFloat(line.unit_price.replace(',', '.')) || 0
          : (line.unit_price ?? 0);

        fields.push({
          label: `Article ${index + 1}`,
          value: `${article.reference} - ${article.designation}`,
        });
        fields.push({
          label: `  Quantité`,
          value: `${quantity}`,
        });
        fields.push({
          label: `  Prix Unitaire`,
          value: `${formatNumber(unitPrice)} MAD`,
        });
        fields.push({
          label: `  Total`,
          value: `${formatNumber(quantity * unitPrice)} MAD`,
        });
      }
    });

    fields.push({ label: '---', value: '---' });
    fields.push({
      label: 'TOTAL GÉNÉRAL',
      value: `${formatNumber(calculateGrandTotal())} MAD`,
      bold: true,
    });

    return fields;
  };

  const handleClose = () => {
    // Reset form
    setFormData({
      date: new Date().toISOString().split('T')[0],
      bon_retour_number: '',
      client_id: null,
    });
    setArticleLines([
      {
        article_id: 0,
        quantity: null,
        unit_price: null,
      },
    ]);
    setShowConfirmDialog(false);
    setIsSubmitting(false);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>Modifier le retour de stock (Multi-articles)</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Date"
                  type="date"
                  value={formData.date}
                  onChange={(e) =>
                    setFormData({ ...formData, date: e.target.value })
                  }
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="BON RETOUR N°"
                  value={formData.bon_retour_number}
                  onChange={(e) =>
                    setFormData({ ...formData, bon_retour_number: e.target.value })
                  }
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  options={clients}
                  getOptionLabel={(option) => `${option.code} - ${option.name}`}
                  value={clients.find((c) => c.id === formData.client_id) || null}
                  onChange={(_, newValue) =>
                    setFormData({ ...formData, client_id: newValue?.id || null })
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Client" required />
                  )}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Articles retournés</Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddArticleLine}
                variant="outlined"
                size="small"
              >
                Ajouter un article
              </Button>
            </Box>

            {articleLines.map((line, index) => (
              <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={5}>
                    <Autocomplete
                      options={articles}
                      getOptionLabel={(option) => `${option.reference} - ${option.designation}`}
                      value={articles.find((a) => a.id === line.article_id) || null}
                      onChange={(_, newValue) => handleArticleChange(index, newValue)}
                      renderInput={(params) => (
                        <TextField {...params} label="Article" required size="small" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      label="Quantité"
                      type="text"
                      value={line.quantity ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          handleArticleFieldChange(index, 'quantity', null);
                        } else if (/^\d*[.,]?\d*$/.test(value)) {
                          handleArticleFieldChange(index, 'quantity', value);
                        }
                      }}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value && value !== '') {
                          const numValue = parseFloat(value.replace(',', '.'));
                          if (!isNaN(numValue)) {
                            handleArticleFieldChange(index, 'quantity', numValue);
                          } else {
                            handleArticleFieldChange(index, 'quantity', null);
                          }
                        }
                      }}
                      fullWidth
                      required
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      label="Prix Unit."
                      type="text"
                      value={line.unit_price ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          handleArticleFieldChange(index, 'unit_price', null);
                        } else if (/^\d*[.,]?\d*$/.test(value)) {
                          handleArticleFieldChange(index, 'unit_price', value);
                        }
                      }}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value && value !== '') {
                          const numValue = parseFloat(value.replace(',', '.'));
                          if (!isNaN(numValue)) {
                            handleArticleFieldChange(index, 'unit_price', numValue);
                          } else {
                            handleArticleFieldChange(index, 'unit_price', null);
                          }
                        }
                      }}
                      fullWidth
                      required
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      label="Total"
                      value={(() => {
                        const quantity = typeof line.quantity === 'string'
                          ? parseFloat(line.quantity.replace(',', '.')) || 0
                          : (line.quantity ?? 0);
                        const unitPrice = typeof line.unit_price === 'string'
                          ? parseFloat(line.unit_price.replace(',', '.')) || 0
                          : (line.unit_price ?? 0);
                        return formatNumber(quantity * unitPrice);
                      })()}
                      fullWidth
                      size="small"
                      InputProps={{ readOnly: true }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveArticleLine(index)}
                      disabled={articleLines.length === 1}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Box>
            ))}

            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="h6" align="right">
                Total Général: {formatNumber(calculateGrandTotal())} MAD
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Annuler</Button>
          <Button onClick={handleSubmitClick} variant="contained" color="primary">
            Modifier le retour
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        open={showConfirmDialog}
        title="Confirmer la modification"
        message="Veuillez vérifier les informations avant de modifier le retour:"
        fields={getConfirmationFields()}
        onConfirm={handleConfirmSubmit}
        onCancel={() => setShowConfirmDialog(false)}
        confirmText="Confirmer"
        cancelText="Retour"
        loading={isSubmitting}
      />

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />
    </>
  );
};

export default StockReturnEdit;
